// Learner App does not support translation (see /learnerApp/src/App.tsx)
/* eslint-disable i18next/no-literal-string */
import { keyframes } from "@emotion/react";
import {
  Box,
  Button,
  Icon,
  OsCard,
  OsCardMedia,
  OsStack,
  Theme,
  Typography,
} from "@outschool/backpack";
import { faTimes } from "@outschool/icons";
import { Container } from "@outschool/ui-legacy-component-library";
import { useIsMobile } from "@outschool/ui-utils";
import React from "react";

import { Topic } from "./consts";
import useLoadingButtonText from "./useLoadingButtonText";

const FOOTER_CARD_HEIGHT_DESKTOP = "80px";
const FOOTER_CARD_WIDTH_DESKTOP = "120px";
const FOOTER_CARD_HEIGHT_MOBILE = "60px";
const FOOTER_CARD_WIDTH_MOBILE = "80px";

const LOADING_BUTTON_GRADIENT_SHIFT_ANIMATION = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;
interface MixFooterProps {
  selectedTopics: Topic[];
  isGeneratingTopic: boolean;
  onStartMixing: () => void;
  onClearAllTopics: () => void;
  onClearTopic: (uid: string) => void;
}

export default function MixFooter({
  selectedTopics,
  isGeneratingTopic = false,
  onStartMixing,
  onClearAllTopics,
  onClearTopic,
}: MixFooterProps) {
  const isMobile = useIsMobile();
  const loadingButtonText = useLoadingButtonText(isGeneratingTopic);
  return (
    <Box
      sx={(theme: Theme) => ({
        position: "fixed",
        bottom: 0,
        left: 0,
        width: "100vw",
        height: "120px",
        zIndex: 11, // MobileFooterTabBar has zIndex = 10, so this needs to be higher
        backgroundColor: "primary.50",
        boxShadow: theme.boxShadows[400],
        padding: "16px",
        [theme.breakpoints.down("sm")]: {
          height: "180px",
        },
      })}
    >
      <Container>
        <OsStack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 4, md: 12 }}
          alignItems={{ xs: "flex-start", md: "center" }}
        >
          <Typography
            variant="h6"
            sx={(theme: Theme) => ({
              [theme.breakpoints.up("md")]: { textWrap: "nowrap" },
            })}
          >
            Choose 2 Topics to Mix
          </Typography>
          <OsStack direction="row" spacing={4}>
            <MixFooterTopicCard
              topic={selectedTopics[0]}
              onClearTopic={onClearTopic}
            />
            <MixFooterTopicCard
              topic={selectedTopics[1]}
              onClearTopic={onClearTopic}
            />
          </OsStack>
          <OsStack
            direction="row"
            spacing={4}
            justifyContent={{ xs: "space-between", md: "flex-start" }}
            fullWidth={true}
          >
            <Button
              onClick={onClearAllTopics}
              variant="outlined"
              color="primary"
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
            >
              Clear
            </Button>
            <Button
              onClick={onStartMixing}
              variant="contained"
              color="primary"
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
              disabled={isGeneratingTopic}
              sx={(theme: Theme) => ({
                [theme.breakpoints.up("md")]: {
                  width: "160px",
                },
                "&.Mui-disabled": {
                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                  backgroundSize: "400% 100%",
                  animation: `${LOADING_BUTTON_GRADIENT_SHIFT_ANIMATION} 2s ease infinite`,
                  color: theme.palette.common.white,
                },
              })}
            >
              {isGeneratingTopic ? loadingButtonText : "Start Mixing"}
            </Button>
          </OsStack>
        </OsStack>
      </Container>
    </Box>
  );
}

function MixFooterTopicCard({
  topic,
  onClearTopic,
}: {
  topic?: Topic;
  onClearTopic: (uid: string) => void;
}) {
  if (!topic) {
    return (
      <Box
        flex
        sx={(theme: Theme) => ({
          justifyContent: "center",
          alignItems: "center",
          width: `calc(${FOOTER_CARD_WIDTH_DESKTOP} + 2px)`,
          height: `calc(${FOOTER_CARD_HEIGHT_DESKTOP} + 2px)`,
          borderRadius: "16px",
          borderWidth: "2px",
          borderStyle: "dashed",
          borderColor: "neutral.200",
          backgroundColor: "common.white",
          padding: "4px",
          [theme.breakpoints.down("sm")]: {
            width: `calc(${FOOTER_CARD_WIDTH_MOBILE} + 2px)`,
            height: `calc(${FOOTER_CARD_HEIGHT_MOBILE} + 2px)`,
          },
        })}
      >
        <Typography variant="subtitle2" sx={{ textAlign: "center" }}>
          Add a Topic
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative" }}>
      <OsCard
        selected={true}
        padding={0}
        onClick={(e: React.MouseEvent<HTMLDivElement>) => {
          e.stopPropagation();
          onClearTopic(topic.uid);
        }}
      >
        <OsCardMedia
          width={{
            xs: FOOTER_CARD_WIDTH_MOBILE,
            md: FOOTER_CARD_WIDTH_DESKTOP,
          }}
          height={{
            xs: FOOTER_CARD_HEIGHT_MOBILE,
            md: FOOTER_CARD_HEIGHT_DESKTOP,
          }}
          alt={topic.title}
          image={topic.attachmentUrl}
          interactiveEffect="zoom"
        />
        <Icon
          icon={faTimes}
          size="medium"
          color="disabled"
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            zIndex: 1,
            backgroundColor: "common.white",
            opacity: 0.8,
            borderRadius: "50%",
            borderWidth: "1px",
            borderStyle: "solid",
            borderColor: "neutral.200",
            padding: "4px",
          }}
        />
      </OsCard>
    </Box>
  );
}
